import {t} from 'i18next';

import {config} from 'config/env';
import {Product, ProductSearchResult} from 'types';
import {IPriceCalculationInputSlabPriceTier} from 'types/recommedation-types/priceCalculation';
import {getDiscountDetailFromSlabTiers} from 'utils';
import {getWholeNumberOrTwoDecimal} from 'utils/common/shopify.util';
import {formatCurrency} from 'utils/recommendation-utils/formatCurrency.util';

export type ProductPriceAndText = {
  product: Product | ProductSearchResult;
  productCount: number;
  defaultSlabPriceTire?: boolean;
  isMobile?: boolean;
};
export interface FormattedCurrency {
  stringValue: string;
  currencySymbol: string;
  currencyCode: string;
  placement: 'start' | 'end';
  decimalPlaces: number;
  numberValue: number;
}

const calculateDiscountPercentage = (originalPrice: number, discountedPrice: number): number => {
  const discountPercentage = ((originalPrice - discountedPrice) / originalPrice) * 100;
  return parseFloat(discountPercentage.toFixed(2));
};

const getDiscountPercentage = (
  product: Product | ProductSearchResult,
  getNumber?: boolean
): string | number | undefined => {
  let discountPercentage = 0;
  const tierOne = (product?.slabPrices?.slabPriceTiers?.find((tier) => tier.tier === 1) ||
    null) as IPriceCalculationInputSlabPriceTier;
  const displayPrice = Number(product?.displayPrice || 0);
  const originPrice = Number(product?.originPrice || 0);
  if (tierOne) {
    discountPercentage = calculateDiscountPercentage(originPrice, tierOne?.priceInVat);
  } else {
    discountPercentage = calculateDiscountPercentage(originPrice, displayPrice);
  }

  const finalDiscountPercentage = formatCurrency({
    amount: discountPercentage,
    featureName: 'product-card'
  }).numberValue;

  if (getNumber) {
    return finalDiscountPercentage;
  }

  return finalDiscountPercentage ? `-${Math.trunc(finalDiscountPercentage)}%` : undefined;
};

const getDisplayPrice = (product: Product | ProductSearchResult) => {
  let displayPrice = 0;
  const showKg = config.RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED && product.isWeightedFresh;
  if (
    product?.slabPrices &&
    Object.keys(product?.slabPrices)?.length > 0 &&
    product?.slabPrices?.slabPriceTiers &&
    product?.slabPrices?.slabPriceTiers?.length > 0
  ) {
    const tierOne = (product?.slabPrices?.slabPriceTiers?.find((tier) => tier.tier === 1) ||
      null) as IPriceCalculationInputSlabPriceTier;
    displayPrice = tierOne?.priceInVat || 0;
  } else {
    displayPrice = product?.displayPrice || 0;
  }

  displayPrice = showKg && product?.moq ? displayPrice / product.moq : displayPrice;
  return formatCurrency({
    amount: displayPrice,
    featureName: 'product-card'
  });
};

const getSlabPrice = (product: Product | ProductSearchResult, isMobile?: boolean) => {
  const showKg = config.RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED && product.isWeightedFresh;
  const slabPriceText = showKg ? t('labels.kg') : t('wholesale.units');
  const tierOne = (product?.slabPrices?.slabPriceTiers?.find((tier) => tier.tier === 1) ||
    null) as IPriceCalculationInputSlabPriceTier;
  const tierTwo = (product?.slabPrices?.slabPriceTiers?.find((tier) => tier.tier === 2) ||
    null) as IPriceCalculationInputSlabPriceTier;
  if (tierOne && tierTwo) {
    return isMobile
      ? `${tierOne?.quantity} - ${tierTwo?.quantity - 1} ${slabPriceText}`
      : `${t('wholesale.buy')} ${tierOne?.quantity} - ${tierTwo?.quantity - 1} ${slabPriceText}`;
  } else if (tierOne && !tierTwo) {
    return isMobile
      ? `${tierOne?.quantity}+ ${showKg ? t('labels.kg') : t('wholesale.units')}`
      : `${t('wholesale.buy')} ${tierOne?.quantity}+ ${showKg ? t('labels.kg') : t('wholesale.units')} `;
  }
  return undefined;
};

const getOriginalPrice = (product: Product | ProductSearchResult): FormattedCurrency => {
  const showKg = config.RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED && product.isWeightedFresh;
  const originPrice = showKg && product.moq ? product.originPrice / product.moq : product.originPrice;
  return formatCurrency({
    amount: originPrice || 0,
    featureName: 'product-card'
  });
};

export const useProductPriceAndText = (props: ProductPriceAndText) => {
  const {product, productCount, defaultSlabPriceTire = false, isMobile} = props;

  const moq = product?.moq || 1;
  const slabPriceTiers = product.slabPrices?.slabPriceTiers || [];
  const isSlabPrice = (slabPriceTiers.length ?? 0) > 0;
  const showKg = product.isWeightedFresh;
  const quantity = showKg ? getWholeNumberOrTwoDecimal(productCount * moq) : productCount;
  const slabPriceQuantityParam = defaultSlabPriceTire ? 0 : quantity;
  const dynamicDiscount = getDiscountDetailFromSlabTiers(slabPriceTiers, slabPriceQuantityParam, product.displayPrice);
  const priceValue = dynamicDiscount.priceValue;
  const kgText = showKg ? ` /${t('labels.kg')}` : '';
  const productDisplayPrice = getDisplayPrice(product);
  const discountPercentage = getDiscountPercentage(product, false);
  const slabPriceInfoText = getSlabPrice(product, isMobile);
  const productOriginalPrice = getOriginalPrice(product);
  const isDiscounted = !!discountPercentage;
  const priceUnit = product.priceUnit;

  return {
    slabPriceTiers,
    isSlabPrice,
    showKg,
    kgText,
    priceValue,
    productDisplayPrice,
    discountPercentage,
    slabPriceInfoText,
    productOriginalPrice,
    isDiscounted,
    quantity,
    priceUnit
  };
};
